export function renderGenUpdateContentServerActionTemplate({
  ctxTableVar,
  ctxCacheInvalidationFn,
  ctxCamelPlural,
  ctxSingular,
  kebabSingular,
  snapshotImport,
  lifecycleHookImport,
  markdownImport,
  updateContext,
  beforeHook,
  updateBeforePublishBlock,
  slugSourceValue,
  toKebabCase,
  ctxFieldMeta,
  htmlUpdateBlock,
  previousSnapshot,
  ctxPlural,
  updateResultContext,
  updateAfterPublishBlock,
  afterHook,
  ctxCamelSingular,
  ctxSingularText
}: {
  ctxTableVar: string
  ctxCacheInvalidationFn: string
  ctxCamelPlural: string
  ctxSingular: string
  kebabSingular: string
  snapshotImport: string
  lifecycleHookImport: string
  markdownImport: string
  updateContext: string
  beforeHook: string
  updateBeforePublishBlock: string
  slugSourceValue: string
  toKebabCase: string
  ctxFieldMeta: string
  htmlUpdateBlock: string
  previousSnapshot: string
  ctxPlural: string
  updateResultContext: string
  updateAfterPublishBlock: string
  afterHook: string
  ctxCamelSingular: string
  ctxSingularText: string
}): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctxTableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctxCamelPlural}CacheTags } from './types'
import { get${ctxSingular}ById } from './get-${kebabSingular}-by-id'
import { getChangedFieldNames } from '@admin/utils/audit/get-changed-field-names'
import {
  build${ctxSingular}RestoreData,
  get${ctxSingular}SlugRetryLimit,
  is${ctxSingular}SlugConflict,
  resolveUnique${ctxSingular}Slug,
  ${snapshotImport},
  has${ctxSingular}VersionChanges
} from './helpers'
${lifecycleHookImport}
import type { ${ctxSingular}UpdateInput, ${ctxSingular}UpdateResult, ${ctxSingular} } from './types'${markdownImport}

export async function update${ctxSingular}(input: ${ctxSingular}UpdateInput): Promise<${ctxSingular}UpdateResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const { id, ...updateData } = input
    const actorId = actor.id
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const [previousRow] = await db
      .select()
      .from(${ctxTableVar})
      .where(eq(${ctxTableVar}.id, id))
      .limit(1)

    if (!previousRow) throw new Error('${ctxSingular} not found')

    const lifecycleContext = ${updateContext}
    ${beforeHook}
${updateBeforePublishBlock}
    const requestedUpdateSlug = updateData.slug
    let changedFields: string[] = []
    let result: (typeof ${ctxTableVar}.$inferSelect)[] | null = null
    for (let attempt = 0; attempt < get${ctxSingular}SlugRetryLimit(); attempt += 1) {
      try {
        if (requestedUpdateSlug !== undefined) {
          updateData.slug = await resolveUnique${ctxSingular}Slug(db, {
            requestedSlug: requestedUpdateSlug,
            sourceValue: ${slugSourceValue},
            fallbackPrefix: '${toKebabCase}',
            currentId: id
          })
        }

        const fieldMeta = [
        ${ctxFieldMeta}
        ]

        const processedData: Record<string, unknown> = {}
        for (const [key, value] of Object.entries(updateData)) {
          if (key === 'published') { processedData[key] = value; continue }
          const field = fieldMeta.find(f => f.name === key)
          if (!field) continue
          if (field.type === 'list') {
            processedData[key] = value || []
          } else if (!field.required && ['number', 'decimal'].includes(field.type)) {
            processedData[key] =
              value === undefined ||
              value === null ||
              value === '' ||
              (typeof value === 'number' && Number.isNaN(value))
                ? null
                : value
          } else if (!field.required && ['date', 'timestamp', 'time', 'string', 'email', 'varchar', 'text', 'select', 'relationship'].includes(field.type)) {
            processedData[key] = value && value !== '' ? value : null
          } else {
            processedData[key] = value
          }
        }
${htmlUpdateBlock}
        changedFields = getChangedFieldNames(
          previousRow as unknown as Record<string, unknown>,
          processedData
        )
        result = await db.transaction(async (tx) => {
          const previousSnapshot = ${previousSnapshot}
          const nextSnapshot = {
            ...previousSnapshot,
            ...build${ctxSingular}RestoreData(processedData)
          }
          const shouldCreateVersion = has${ctxSingular}VersionChanges(previousSnapshot, nextSnapshot)

          if (shouldCreateVersion) {
            await createEntityVersion(tx, {
              entityType: '${ctxPlural}',
              entityId: id,
              data: previousSnapshot,
              createdBy: actorId
            })
          }

          return tx.update(${ctxTableVar})
            .set({ ...processedData, updatedAt: now, updatedBy: actorId })
            .where(eq(${ctxTableVar}.id, id))
            .returning()
        })
        break
      } catch (error) {
        if (is${ctxSingular}SlugConflict(error) && attempt < get${ctxSingular}SlugRetryLimit() - 1) {
          continue
        }
        throw error
      }
    }

    if (!result) throw new Error('${ctxSingular} not found')

    if (result.length === 0) throw new Error('${ctxSingular} not found')

    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.all)
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.byId(id))
    ${ctxCacheInvalidationFn}(entityVersionsCacheTags.for('${ctxPlural}', id))
    const updated${ctxSingular} = await get${ctxSingular}ById(id)
    const updated${ctxSingular}Data = updated${ctxSingular} ?? (result[0] as unknown as ${ctxSingular})
    const updated${ctxSingular}Context = ${updateResultContext}
${updateAfterPublishBlock}
    ${afterHook}

    return {
      success: true,
      ${ctxCamelSingular}: updated${ctxSingular}Data
    }
  } catch (error) {
    console.error('Error updating ${ctxSingularText}:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to update ${ctxSingularText}')
  }
}
`
}
